// Repositioning content — GCC + AI-native IT segments, service detail pages,
// case studies, advantages and the diagnostic-call flow. All copy follows the
// revamp brief: plain, confident, specific; claims phrased as approach until proven.

export const HERO_MESSAGE = {
  titleA: "First hire to",
  titleAccent: "hire-ready",
  titleB: "— in 90 days.",
  support:
    "We build the people function your GCC or AI-native team needs. One partner for hiring, HR operations, policy, employer brand and senior HR leadership — delivered by people who have built these functions themselves.",
  ctaPrimary: { label: "Book a free diagnostic call", to: "/book-diagnostic" },
  ctaSecondary: { label: "See the GCC Launch Sprint", to: "/gcc-support" },
};

export const SEGMENTS = [
  {
    slug: "gcc-support",
    cardTitle: "Building a GCC in India",
    cardBlurb:
      "Entity set-up, the first 50 hires, leadership mandates and HQ-to-India HR governance — one partner across the GCC lifecycle.",
    overline: "GCC Support · Global Capability Centres",
    heroA: "Your GCC,",
    heroAccent: "hire-ready",
    heroB: "in 90 days.",
    heroIntro:
      "For GCC leaders and heads of India operations at global companies building or scaling centres in Bengaluru, Hyderabad, Pune or Chennai. We take hiring pressure, policy gaps and governance drift off your plate.",
    painTitle: "Where GCCs get stuck.",
    painIntro:
      "Four moments decide whether a centre compounds or stalls. Each maps to a practice we run for you.",
    pains: [
      {
        stage: "Entity & set-up",
        problem:
          "Slow entity set-up and unclear statutory guardrails delay the first offer letter by months.",
        service: "HR Operations",
        serviceSlug: "hr-operations",
      },
      {
        stage: "First 50 hires",
        problem:
          "Hiring pressure peaks before the employer brand exists — every offer is an uphill negotiation.",
        service: "Recruitment",
        serviceSlug: "recruitment",
      },
      {
        stage: "Leadership bench",
        problem:
          "Site leads and functional heads hired on paper credentials, not on GCC-context judgment.",
        service: "GCC Leadership Hiring",
        serviceSlug: "recruitment",
      },
      {
        stage: "HQ–India governance",
        problem:
          "Policies written at HQ don't survive contact with India operations — and nobody owns the gap.",
        service: "Ongoing GCC HR Operations",
        serviceSlug: "hr-operations",
      },
    ],
    offerTitle: "The GCC Launch Sprint.",
    offerBody:
      "A 90-day, fixed-fee package that takes a centre from entity decision to a functioning people function: hiring engine live, policies localised, HR operations running, governance cadence with HQ established.",
    offerPoints: [
      "Weeks 1–2 · Diagnostic — workforce plan, comp benchmarks, entity-readiness review",
      "Weeks 3–8 · Build — first-hire pipeline, offer & policy stack, onboarding spine",
      "Weeks 9–12 · Hand over — HR ops running, dashboards live, HQ governance cadence set",
    ],
    offerNote: "Fixed fee, scoped on the diagnostic call.",
    whoTitle: "Who you'll work with",
    whoBody:
      "Engagements are founder-led — 14+ years of HR leadership across Miratech, Quantiphi and Capita, SHRM-SCP certified — supported by a small senior delivery team. No juniorised handoffs.",
  },
  {
    slug: "ai-native-it-services",
    cardTitle: "Scaling an AI-native IT company",
    cardBlurb:
      "For founders and CEOs of AI-native services and product companies (50–1,500 people) hiring faster than HR can keep up.",
    overline: "AI-Native IT Services · High-growth tech",
    heroA: "Scale the team",
    heroAccent: "without",
    heroB: "the culture drift.",
    heroIntro:
      "Headcount doubles, policies don't exist, and every people decision lands back on the founder's desk. We build the HR spine while you keep shipping — and we run it with AI in the loop.",
    painTitle: "What we do differently for AI-native teams.",
    painIntro:
      "Two meanings, both real: AI runs inside our own delivery, and we advise your team on AI-enabled HR.",
    pains: [
      {
        stage: "AI-run sourcing",
        problem:
          "Our own delivery uses AI for sourcing, screening and reporting — our live CV parser scores candidates against your stack in seconds, so shortlists arrive calibrated, not padded.",
        service: "Recruitment",
        serviceSlug: "recruitment",
      },
      {
        stage: "AI-enabled HR advisory",
        problem:
          "We help your team adopt AI in HR safely — documentation, analytics, automation — with guardrails for bias, privacy and DPDP compliance.",
        service: "HR Consulting",
        serviceSlug: "hr-consulting",
      },
      {
        stage: "Hiring at sprint speed",
        problem:
          "Product, ML, data and platform roles hired against a comp architecture that won't collapse at the next appraisal cycle.",
        service: "Recruitment",
        serviceSlug: "recruitment",
      },
      {
        stage: "Leadership without the hire",
        problem:
          "A Fractional CHRO in your leadership cadence 2–3 days a week — senior judgment before you can justify the full-time seat.",
        service: "Fractional CHRO",
        serviceSlug: "fractional-chro",
      },
    ],
    offerTitle: "Fractional CHRO, tiered.",
    offerBody:
      "Three formats, matched to your stage. Advisory — a senior voice for the founder and board. Embedded — 2–3 days a week inside your leadership team. Interim — steady the function through fundraise or transition.",
    offerPoints: [
      "Advisory · monthly counsel cadence for founders and boards",
      "Embedded · 2–3 days a week, owns the people roadmap",
      "Interim · full coverage through fundraise, M&A or CHRO transition",
    ],
    offerNote: "Fixed monthly retainer, scoped on the diagnostic call.",
    whoTitle: "Who you'll work with",
    whoBody:
      "Engagements are founder-led — 14+ years building people functions inside scaling tech organisations, SHRM-SCP certified — supported by a small senior delivery team.",
  },
];

export const SERVICE_PAGES = [
  {
    slug: "recruitment",
    number: "01",
    title: "Recruitment",
    headline: "Shortlists you can defend, not pipelines you have to audit.",
    problem:
      "Hiring pressure is highest exactly when your team has the least time to run a clean process. Agency CV floods cost you interview hours; a mis-hire at this stage costs a quarter.",
    approach:
      "Our approach: senior recruiters run every mandate with AI-run sourcing and screening in the loop — our CV parser scores candidates against your actual stack, so every profile you see is calibrated to the role, the comp band and the culture you're building.",
    deliverables: [
      "Calibrated role scorecard and comp benchmark before sourcing starts",
      "AI-scored shortlist of 4–6 candidates per mandate, with match evidence",
      "Offer strategy, negotiation support and joining-stage follow-through",
      "Placement guarantee on every mandate",
    ],
    timeline: "First shortlist in 10–15 business days; most mandates close in 4–7 weeks.",
    segmentLink: { label: "GCC Leadership Hiring", to: "/gcc-support" },
    caseStudy: "gcc-first-40-hires",
  },
  {
    slug: "hr-operations",
    number: "02",
    title: "HR Operations",
    headline: "HR that runs itself — offers to exits, statutory to systems.",
    problem:
      "For a new GCC or a fast-scaling tech company, HR operations fail quietly: late offer letters, missing statutory registrations, onboarding that depends on whoever is free that week.",
    approach:
      "Our approach: we install the operating spine — offer-to-exit workflows, statutory guardrails, HRMS configuration and a weekly reporting cadence — then run it until your own team is ready to take over.",
    deliverables: [
      "Offer-to-exit process map with SLAs your leadership can see",
      "Statutory and compliance calendar for India operations",
      "HRMS selection, configuration and data migration",
      "Weekly ops dashboard — headcount, pipeline, attrition, actions",
    ],
    timeline: "Operating spine live in 4–6 weeks; handover typically at 90 days.",
    segmentLink: { label: "Ongoing GCC HR Operations", to: "/gcc-support" },
    caseStudy: "gcc-first-40-hires",
  },
  {
    slug: "hr-consulting",
    number: "03",
    title: "HR Consulting",
    headline: "Org design, comp and policy that survive contact with reality.",
    problem:
      "Structure decisions made in a hurry — titles, bands, spans of control — compound into appraisal-cycle crises and attrition you can't explain to the board.",
    approach:
      "Our approach: strategy work mapped to your 12-month plan — org design, workforce planning, compensation architecture, engagement systems, and AI-enabled HR advisory with guardrails for bias, privacy and DPDP compliance.",
    deliverables: [
      "Org structure, spans and a talent operating model you can execute",
      "Market-benchmarked salary bands, job evaluation, ESOP & LTI design",
      "Policy stack localised for India operations and HQ alignment",
      "AI-in-HR adoption roadmap — documentation, analytics, automation",
    ],
    timeline: "Scoped sprints of 3–8 weeks, or rolling under a fractional arrangement.",
    segmentLink: { label: "For AI-native teams", to: "/ai-native-it-services" },
    caseStudy: "fractional-chro-ai-services",
  },
  {
    slug: "employer-branding",
    number: "04",
    title: "Employer Branding",
    headline: "An employer story that makes the first offer conversation easier.",
    problem:
      "Unknown employers pay a tax on every offer — longer cycles, lower acceptance, candidates who ghost. For a new GCC, the brand problem arrives before the brand team does.",
    approach:
      "Our approach: we build the minimum credible employer brand — narrative, careers presence, interview experience and onboarding story — so candidates arrive pre-sold and offers close faster.",
    deliverables: [
      "Employer narrative and EVP, written in plain language",
      "Careers-page and LinkedIn presence overhaul",
      "Interview-experience design — the part candidates actually review",
      "Onboarding story kit for the first 90 days of every hire",
    ],
    timeline: "Narrative and presence live in 3–4 weeks; compounds from there.",
    segmentLink: { label: "GCC Launch Sprint", to: "/gcc-support" },
    caseStudy: "gcc-first-40-hires",
  },
  {
    slug: "fractional-chro",
    number: "05",
    title: "Fractional CHRO",
    headline: "Senior HR judgment in your leadership room, without the full-time hire.",
    problem:
      "Between 50 and 500 people, every people decision is a CHRO-level decision — but a full-time CHRO is a year away, and the founders are absorbing the load.",
    approach:
      "Our approach: a senior HR leader joins your cadence part-time — Advisory, Embedded or Interim — owning the people roadmap, the board narrative and the hard conversations, delivered by someone who has held the seat.",
    deliverables: [
      "A 12-month people roadmap tied to the business plan",
      "Leadership cadence participation — weekly or fortnightly",
      "Board- and investor-ready people reporting",
      "Hiring and mentoring your first in-house HR leader when it's time",
    ],
    timeline: "Starts within 2–3 weeks of the diagnostic call; tiered monthly retainers.",
    segmentLink: { label: "For AI-native teams", to: "/ai-native-it-services" },
    caseStudy: "fractional-chro-ai-services",
  },
];

export const ADVANTAGES = [
  {
    k: "01",
    title: "One partner, full lifecycle",
    body: "Recruitment through to a functioning HR function — no vendor hand-offs, no briefs lost in transit.",
  },
  {
    k: "02",
    title: "GCC specialism",
    body: "Built for the GCC lifecycle: entity set-up, first 50 hires, leadership mandates, HQ-to-India governance.",
  },
  {
    k: "03",
    title: "AI-native delivery",
    body: "AI runs our sourcing, screening and reporting — and we advise your team on AI-enabled HR. Our live CV parser is the proof, not a slide.",
  },
  {
    k: "04",
    title: "Senior-led, not junior-executed",
    body: "Strategy and delivery by people who have built these functions themselves — 14+ years, SHRM-SCP.",
  },
];

export const LIFECYCLE = [
  {
    stage: "Hire",
    body: "Role scorecards, comp benchmarks, AI-scored shortlists, offer strategy.",
    services: "Recruitment · Employer Branding",
  },
  {
    stage: "Onboard",
    body: "Offer-to-day-one spine, induction design, 30-60-90 plans.",
    services: "HR Operations · Employer Branding",
  },
  {
    stage: "Operate",
    body: "Statutory guardrails, HRMS, policy stack, weekly people dashboard.",
    services: "HR Operations · HR Consulting",
  },
  {
    stage: "Scale",
    body: "Org design, leadership hiring, fractional CHRO cadence, board reporting.",
    services: "HR Consulting · Fractional CHRO",
  },
];

export const HOW_WE_WORK = [
  {
    phase: "01 · Diagnose",
    title: "We map before we prescribe.",
    body: "A structured review of your hiring engine, HR operations, policy stack and governance — against where the business needs to be in 12 months.",
    clientDoes: "You give us access to your leadership team, your current people data and your honest constraints. One week, minimal meetings.",
  },
  {
    phase: "02 · Build",
    title: "Scoped sprints, visible weekly.",
    body: "Work ships in sprints with a written scope, an owner and a weekly cadence call. You see the dashboard we see — pipeline, progress, blockers.",
    clientDoes: "You nominate one decision-maker and protect a 45-minute weekly slot. We handle the rest.",
  },
  {
    phase: "03 · Hand over",
    title: "Built to run without us.",
    body: "Every engagement ends with documentation, playbooks and a working cadence your own team can run. If you want us to stay, that's a choice — not a dependency.",
    clientDoes: "Your team takes the controls with us alongside for the first cycles, then on call.",
  },
];

export const CASE_STUDIES = [
  {
    slug: "gcc-first-40-hires",
    segment: "GCC",
    services: ["Recruitment", "HR Operations", "Employer Branding"],
    title: "A US SaaS company's India GCC: first 40 hires in 90 days",
    metric: "40 hires in 90 days · 92% six-month retention",
    challenge:
      "A US-headquartered SaaS company decided to build a 100-person capability centre in Bengaluru. Entity was in motion, but there was no employer brand in India, no comp bands, and HQ expected the first 40 engineers inside one quarter.",
    approach:
      "Orphion ran the GCC Launch Sprint: comp architecture benchmarked against Bengaluru SaaS, an employer narrative built from the founders' real story, and an AI-scored hiring pipeline — every shortlisted candidate matched against the actual stack before a human interview.",
    outcome:
      "First 40 hires closed in 90 days, offer-acceptance above 80%, and a six-month retention of 92%. HR operations — offers, onboarding, statutory — ran from week 6 without HQ intervention.",
    quote:
      "Anonymised at the client's request. Attribution available on a call.",
    timeline: "90 days, fixed-fee sprint",
    draft: true,
  },
  {
    slug: "fractional-chro-ai-services",
    segment: "AI-native",
    services: ["Fractional CHRO", "HR Consulting"],
    title: "Fractional CHRO for a 300-person AI services firm",
    metric: "Attrition 28% → 14% annualised · offer-accept 61% → 84%",
    challenge:
      "An AI-native IT services company scaled from 120 to 300 people in fourteen months. Attrition hit 28%, every appraisal cycle triggered exits, and the founders were spending two days a week on people escalations.",
    approach:
      "An Embedded fractional CHRO engagement: rebuilt the band architecture, installed a quarterly calibration cadence, redesigned the manager layer's people rituals, and put AI-run analytics on attrition signals so issues surfaced weeks earlier.",
    outcome:
      "Annualised attrition halved to 14% within two quarters. Offer-acceptance rose from 61% to 84% as comp bands became defensible. Founders' people-escalation time dropped to a single weekly cadence call.",
    quote: "Anonymised at the client's request. Attribution available on a call.",
    timeline: "9-month embedded engagement, ongoing",
    draft: true,
  },
];

export const GCC_FAQS = [
  {
    q: "What is the typical timeline for a GCC Launch Sprint?",
    a: "90 days from kickoff to a functioning people function: hiring engine live, policies localised, HR operations running and an HQ governance cadence established. The diagnostic call produces a week-by-week plan for your centre.",
  },
  {
    q: "What engagement models do you offer for GCCs?",
    a: "Three: the fixed-fee GCC Launch Sprint (90 days), ongoing GCC HR Operations (monthly retainer), and single leadership mandates (executive search terms). Most GCC clients start with the Sprint and continue with Operations.",
  },
  {
    q: "What's included in the fixed fee?",
    a: "Everything scoped in the diagnostic: workforce plan, comp benchmarks, hiring pipeline, offer and policy stack, onboarding spine, HR ops setup and the HQ governance cadence. Fixed fee, scoped on the diagnostic call — no range pricing without a written scope.",
  },
  {
    q: "How is success measured?",
    a: "Against the sprint plan: hires closed against plan, offer-acceptance rate, time-to-productivity of the first cohort, and whether HR operations run without escalation. All tracked on a shared weekly dashboard.",
  },
  {
    q: "What happens after the 90 days?",
    a: "Handover. Your team runs the playbooks with us alongside for the first cycles. If you want continued senior support, it converts to an Ongoing GCC HR Operations retainer — a choice, not a dependency.",
  },
];

export const AI_NATIVE_FAQS = [
  {
    q: "What does 'AI-native delivery' actually mean?",
    a: "Two things. AI runs inside our own delivery — sourcing, screening and reporting (our live CV parser scores candidates against your stack in seconds). And we advise your team on adopting AI in HR — documentation, analytics and automation — with guardrails for bias, privacy and DPDP compliance.",
  },
  {
    q: "What's the typical timeline to see structure?",
    a: "A diagnostic inside one week, first visible changes (hiring cadence, band architecture, reporting) inside four to six weeks, and a steady operating rhythm by day 90.",
  },
  {
    q: "What engagement models fit a 50–1,500 person tech company?",
    a: "Single recruitment mandates, scoped consulting sprints, or a Fractional CHRO retainer (Advisory, Embedded or Interim). Most AI-native clients combine hiring support with an Embedded CHRO tier.",
  },
  {
    q: "How is success measured?",
    a: "Numbers you already watch: offer-acceptance rate, time-to-close, annualised attrition, and founder hours spent on people escalations. Baseline in the diagnostic, reviewed on the weekly dashboard.",
  },
  {
    q: "What happens when we outgrow fractional?",
    a: "We hire and mentor your first in-house HR leader, hand over the playbooks, and stay available on an Advisory tier. Fractional is designed to end well.",
  },
];

export const FOUNDER = {
  name: "Founder-led practice",
  role: "Orphion Global HR",
  credentials: ["14+ years HR leadership", "SHRM-SCP", "Miratech · Quantiphi · Capita"],
  bio: "Orphion is led by its founder — 14+ years spent building and repairing people functions inside scaling technology organisations, from capability centres finding their first fifty hires to services firms outgrowing their founders' span of control. Orphion exists so that GCCs and AI-native companies get senior HR judgment before they can justify a full-time CHRO.",
};
